type FetchBatch<K, V> = (keys: K[]) => Promise<Map<K, V>>

interface BatchLoaderOptions {
  delay?: number
}

export function createBatchLoader<K, V>(fetchBatch: FetchBatch<K, V>, opts?: BatchLoaderOptions) {
  const delay = opts?.delay ?? 16
  const cache = new Map<K, V>()
  let pending: Array<{ key: K; resolve: (v: V) => void; reject: (e: unknown) => void }> = []
  let scheduled = false

  function flush() {
    scheduled = false
    const batch = pending
    pending = []
    const keys = batch.map((b) => b.key)
    fetchBatch(keys)
      .then((results) => {
        for (const entry of batch) {
          const value = results.get(entry.key)
          if (value !== undefined) {
            cache.set(entry.key, value)
            entry.resolve(value)
          } else {
            // No result for this key — resolve with empty default
            entry.resolve(undefined as unknown as V)
          }
        }
      })
      .catch((err) => {
        for (const entry of batch) entry.reject(err)
      })
  }

  function load(key: K): Promise<V> {
    const cached = cache.get(key)
    if (cached !== undefined) return Promise.resolve(cached)
    return new Promise<V>((resolve, reject) => {
      pending.push({ key, resolve, reject })
      if (!scheduled) {
        scheduled = true
        setTimeout(flush, delay)
      }
    })
  }

  function invalidate(key: K) {
    cache.delete(key)
  }

  function clear() {
    cache.clear()
  }

  return { load, invalidate, clear }
}
